import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { useContentAppearance } from '@confluence/content-appearance/entry-points/ContentAppearanceContext.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isStatusCodeError } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';
import { containsNetworkError } from '@confluence/network';
import { useUpdateContentProperty } from '@confluence/update-content-property-utils/entry-points/useUpdateContentProperty.ts';
import { useContentType } from '@confluence/page-context';
import { expValEquals } from '@confluence/feature-experiments';

import { EditorAppearanceButtonComponent } from './EditorAppearanceButtonComponent.tsx';
import type { UpdateContentAppearancePropertyBody } from './editorAppearanceTypes.ts';
// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { UpdateContentAppearancePropertyMutation } from './UpdateContentAppearancePropertyMutation.experimentalgraphql.ts';
import { EditorAppearanceDropdownComponent } from './EditorAppearanceDropdownComponent.tsx';

export const i18n = defineMessages({
	errorFlagTitle: {
		id: 'editor-appearance-button.error.flag.title',
		description:
			'Title for the error flag shown when there is a conflict updating the width of the content',
		defaultMessage: 'Unable to update width',
	},
	unauthorizedErrorFlagBody: {
		id: 'editor-appearance-button.unauthorized.error.flag.body',
		description:
			'Body text for the error flag shown when there is unauthorized error updating the width of the content',
		defaultMessage:
			'You don’t have permission to update the width. Contact your admin to request access.',
	},
	networkErrorFlagBody: {
		id: 'editor-appearance-button.network.error.flag.body',
		description: "Message for error flags when an error with a user's internet connection occurs",
		defaultMessage: 'Check your internet connection, refresh, and try again.',
	},
});

export type EditorAppearanceButtonNextProps = {
	contentId: string;
	isStatusDisconnected: boolean;
	flags: FlagsStateContainer;
	disabled?: boolean;
	setIsAppearanceDropdownOpen?: (isOpen: boolean) => void;
	isAppearanceDropdownOpen?: boolean;
};

const EditorAppearanceButtonNextComponent = ({
	contentId,
	isStatusDisconnected,
	flags,
	disabled = false,
	setIsAppearanceDropdownOpen,
	isAppearanceDropdownOpen,
}: EditorAppearanceButtonNextProps) => {
	const { contentAppearance, setContentAppearance, contentAppearanceLoading } =
		useContentAppearance();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [{ collabEditProvider, collabEditInitialized }] = useNativeCollabState();
	const [contentType] = useContentType();
	const { updateContentProperty, processUpdateContentPropertyResult } = useUpdateContentProperty({
		contentId,
		contentType: contentType || 'page',
	});
	const updateContentAppearanceProperty = async (
		params: UpdateContentAppearancePropertyBody,
	): Promise<void> => {
		const client = getApolloClient();
		const payload = {
			contentId: params.contentId,
			propertyKey: params.propertyKey,
			propertyValue: params.propertyValue,
			versionNumber: params.versionNumber.toString(),
			isDelete: false,
		};

		try {
			if (expValEquals('migrate_off_experimentalupdatecontentproperty', 'isEnabled', true)) {
				const result = await updateContentProperty({
					propertyKey: params.propertyKey,
					propertyValue: params.propertyValue,
					isDelete: false,
					hasExistingProperty: contentAppearance.version > 0,
				});
				const responseData = processUpdateContentPropertyResult(result?.data);
				if (responseData?.success) {
					setContentAppearance({
						appearance: params.propertyValue,
						version: params.versionNumber,
					});
				} else {
					if (responseData?.errors?.[0]) {
						createAnalyticsEvent({
							type: 'sendOperationalEvent',
							data: {
								action: 'failed',
								actionSubject: 'updateContentAppearanceProperty',
								source: 'EditorAppearanceButtonNext',
								attributes: {
									error: responseData.errors[0],
								},
							},
						}).fire();
						const statusCode = responseData.errors[0].extensions?.statusCode;
						if (String(statusCode) === '409') {
							// Not showing flag for 409s since it's not a user error but something that's happening on our end
							return;
						} else {
							const flagBodyText =
								String(statusCode) === '403'
									? i18n.unauthorizedErrorFlagBody
									: i18n.networkErrorFlagBody;
							void flags.showErrorFlag({
								id: 'content-appearance-button-conflict-error-flag',
								title: <FormattedMessage {...i18n.errorFlagTitle} />,
								description: <FormattedMessage {...flagBodyText} />,
							});
						}
					}
				}
			} else {
				// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
				const result = await client.mutate({
					mutation: UpdateContentAppearancePropertyMutation,
					variables: payload,
				});

				if (result?.data?.experimentalUpdateContentProperty?.success) {
					setContentAppearance({
						appearance: params.propertyValue,
						version: params.versionNumber,
					});
				}
			}
		} catch (error) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'updateContentAppearanceProperty',
					source: 'EditorAppearanceButtonNext',
					attributes: {
						error,
					},
				},
			}).fire();
			const is409Error = isStatusCodeError(error, '409');
			const is403Error = isUnauthorizedError(error);
			const isNetworkError = containsNetworkError(error);

			// Not showing flag for 409s since it's not a user error but something that's happening on our end
			if (is409Error) {
				markErrorAsHandled(error);
				return;
			}

			if (is403Error || isNetworkError) {
				markErrorAsHandled(error);
				const flagBodyText = is403Error
					? i18n.unauthorizedErrorFlagBody
					: i18n.networkErrorFlagBody;

				void flags.showErrorFlag({
					id: 'content-appearance-button-conflict-error-flag',
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...flagBodyText} />,
				});
			}
		}
	};

	const isDisabled = contentAppearanceLoading || !collabEditInitialized || disabled;

	if (
		expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) ||
		expValEquals('confluence_max_width_content_appearance', 'isEnabled', true)
	) {
		return (
			<EditorAppearanceDropdownComponent
				contentId={contentId}
				collabEditProvider={collabEditProvider}
				isDisabled={isDisabled}
				isStatusDisconnected={isStatusDisconnected}
				updateContentAppearanceProperty={updateContentAppearanceProperty}
				contentAppearanceDraft={contentAppearance}
				setContentAppearance={setContentAppearance}
				setIsAppearanceDropdownOpen={setIsAppearanceDropdownOpen}
				isAppearanceDropdownOpen={isAppearanceDropdownOpen}
			/>
		);
	}

	return (
		<EditorAppearanceButtonComponent
			contentId={contentId}
			collabEditProvider={collabEditProvider}
			isDisabled={isDisabled}
			isStatusDisconnected={isStatusDisconnected}
			updateContentAppearanceProperty={updateContentAppearanceProperty}
			contentAppearanceDraft={contentAppearance}
			setContentAppearance={setContentAppearance}
		/>
	);
};

export const EditorAppearanceButtonNext = withFlags(EditorAppearanceButtonNextComponent);
